/**
 * 运营账户权限位清单 (唯一数据源)
 *
 * 后端 admin.js 的 PATH_PERMISSIONS 按「组名 + .read/.write」判定,
 *   此处的 key 必须与后端保持一致, 否则勾选了也不生效。
 * OpsUsers.vue 的权限配置项、Layout.vue 的菜单可见性均从这里取。
 */
export const PERMISSIONS = {
  // Read 类别
  'admin.channels.read':        '查看渠道',
  'admin.deposits.read':        '查看入账',
  'admin.expenses.read':        '查看支出',
  'admin.invoices.read':        '查看发票',
  'admin.invoice.archive.read': '查看发票存档',
  'admin.analytics.read':       '数据看板',
  'admin.reports.read':         '财务报表',
  'admin.drivers.read':         '查看司机',
  'admin.subsidies.read':       '查看奖励',
  'admin.logs.read':            '操作日志',
  // Write 类别
  'admin.channels.write':       '管理渠道',
  'admin.deposits.write':       '审核入账',
  'admin.expenses.write':       '审批支出',
  'admin.invoices.write':       '处理发票',
  'admin.invoice.archive.write':'导入发票存档',
  'admin.drivers.write':        '管理司机',
  'admin.subsidies.write':      '发放奖励',
  // 财务模块 (独立权限位, 走 requireFinance 中间件)
  'fin.invoice.verify':         '发票核销 (财务工作台)'
};

// 权限配置分组 (运营账户管理页按此渲染)
export const PERM_GROUPS = [
  { name: '渠道管理', key: 'channel', items: [
    { key: 'admin.channels.read',  label: '查看渠道',  note: '渠道管理列表' },
    { key: 'admin.channels.write', label: '管理渠道',  note: '新建/编辑渠道' }
  ] },
  { name: '资金审核', key: 'finance', items: [
    { key: 'admin.deposits.read',  label: '查看入账',  note: '入账审核' },
    { key: 'admin.deposits.write', label: '审核入账',  note: '通过/驳回' },
    { key: 'admin.expenses.read',  label: '查看支出',  note: '支出审核' },
    { key: 'admin.expenses.write', label: '审批支出',  note: '通过/驳回' }
  ] },
  { name: '发票管理', key: 'invoice', items: [
    { key: 'admin.invoices.read',        label: '查看发票',     note: '发票受理/明细' },
    { key: 'admin.invoices.write',       label: '处理发票',     note: '受理/录入明细' },
    { key: 'admin.invoice.archive.read', label: '查看发票存档', note: '存档概览/查询' },
    { key: 'admin.invoice.archive.write',label: '导入发票存档', note: '上传库存档表格' }
  ] },
  { name: '数据查看', key: 'analytics', items: [
    { key: 'admin.analytics.read', label: '数据看板',  note: '总览/统计' },
    { key: 'admin.reports.read',   label: '财务报表',  note: '导出报表' }
  ] },
  { name: '司机管理', key: 'driver', items: [
    { key: 'admin.drivers.read',  label: '查看司机', note: '司机备案列表' },
    { key: 'admin.drivers.write', label: '管理司机', note: '录入/编辑司机' }
  ] },
  { name: '资金奖励', key: 'reward', items: [
    { key: 'admin.subsidies.read',  label: '查看奖励', note: '奖励发放列表' },
    { key: 'admin.subsidies.write', label: '发放奖励', note: '新增奖励' }
  ] },
  { name: '财务模块', key: 'fin', items: [
    { key: 'fin.invoice.verify', label: '发票核销 (财务工作台)', note: '核销/驳回' }
  ] },
  { name: '系统', key: 'system', items: [
    { key: 'admin.logs.read', label: '查看操作日志', note: '审计' }
  ] }
];

export const DEFAULT_OPERATOR_PERMS = ['admin.expenses.read', 'admin.expenses.write'];

/**
 * 判断运营账户是否具备某个权限位
 *
 * read 类权限位: 拥有对应 .read 或 .write 都算通过
 *   (与后端 hasPerm 语义一致: 写权限蕴含读权限)
 * @param {Array} perms 权限位数组
 * @param {string} key  需要的权限位, 如 'admin.expenses.read'
 */
export function hasPermKey(perms, key) {
  const p = Array.isArray(perms) ? perms : [];
  if (!key) return false;
  if (p.indexOf(key) >= 0) return true;
  if (key.endsWith('.read')) {
    const writeKey = key.replace(/\.read$/, '.write');
    if (p.indexOf(writeKey) >= 0) return true;
  }
  return false;
}

/**
 * 运营账户登录后的落地页 (按权限自动判断)
 *
 * 之前一律跳 /admin/expenses, 但财务账号 (fin.invoice.verify) 没有支出审核权限,
 *   跳过去会被路由守卫弹回, 表现为"登录后页面报错/空白"。
 *   现按优先级挑第一个有权限的页面:
 *     1) 发票核销  fin.invoice.verify
 *     2) 支出审核  admin.expenses.read
 *     3) 发票受理  admin.invoices.read
 *     4) 发票存档  admin.invoice.archive.read
 *     5) 数据看板  admin.analytics.read
 *     6) 渠道管理  admin.channels.read
 *     7) 入账审核  admin.deposits.read
 *     8) 财务报表  admin.reports.read
 *     9) 司机备案  admin.drivers.read
 *    10) 奖励发放  admin.subsidies.read
 *    11) 操作日志  admin.logs.read
 *   一个权限都没有 → 返回 null (由调用方提示"未分配权限", 不做无效跳转)
 */
export function operatorHomePath(perms) {
  const p = Array.isArray(perms) ? perms : [];
  const has = k => hasPermKey(p, k);
  if (has('fin.invoice.verify')) return '/admin/invoice-verify';
  if (has('admin.expenses.read')) return '/admin/expenses';
  if (has('admin.invoices.read')) return '/admin/invoices';
  if (has('admin.invoice.archive.read')) return '/admin/invoice-archive';
  if (has('admin.analytics.read')) return '/admin/dashboard';
  if (has('admin.channels.read')) return '/admin/channels';
  if (has('admin.deposits.read')) return '/admin/deposits';
  if (has('admin.reports.read')) return '/admin/reports';
  if (has('admin.drivers.read')) return '/admin/drivers';
  if (has('admin.subsidies.read')) return '/admin/subsidies';
  if (has('admin.logs.read')) return '/admin/logs';
  return null;
}
